import { useMemo } from "react";
import useSWR from "swr";

import { CHART_PERIODS, MEXC_CHART_PERIODS } from "lib/legacy";
import { USD_DECIMALS } from "config/factors";
import { MEXC_API_URL, COINGECKO_API_URL } from "config/backend";
import { BSC } from "config/chains";
import { sleep } from "lib/sleep";
import { formatAmount } from "lib/numbers";
import { getNormalizedTokenSymbol, getTokenBySymbolSafe } from "config/tokens";
import { fetchCandles } from "lib/indexer/client";
import type { Bar, FromOldToNewArray } from "../tradingview/types";

// Stock/commodity synthetics that are priced via Yahoo Finance — never send to MEXC
const STOCK_SYMBOLS = new Set(["AAPL", "TSLA", "NVDA", "MSFT", "GOOGL", "AMZN", "META", "SPY", "QQQ", "GOLD"]);
const _bescIndexerBase = ((import.meta.env.VITE_INDEXER_API as string | undefined) || "").replace(/\/+$/, "");

async function getStockChartPrices(symbol: string, period: string, limit = 300): Promise<FromOldToNewArray<Bar>> {
  const url = `${_bescIndexerBase}/api/prices/candles?tokenSymbol=${symbol}&period=${period}&limit=${limit}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Stock candles ${res.status}`);
  const json = await res.json();
  if (!Array.isArray(json.candles) || json.candles.length === 0) throw new Error("No stock candle data");
  // Backend returns newest-first; reverse to oldest-first for FromOldToNewArray
  return json.candles
    .slice()
    .reverse()
    .map(([time, open, high, low, close]: number[]) => ({ time, open, high, low, close }));
}

export const timezoneOffset = -new Date().getTimezoneOffset() * 60;

type Price = {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
};


export function fillGaps(prices, periodSeconds) {
  if (prices.length < 2) {
    return prices;
  }

  const newPrices = [prices[0]];
  let prevTime = prices[0].time;
  for (let i = 1; i < prices.length; i++) {
    const { time, open } = prices[i];
    if (prevTime) {
      let j = (time - prevTime) / periodSeconds - 1;
      while (j > 0) {
        newPrices.push({
          time: time - j * periodSeconds,
          open,
          close: open,
          high: open * 1.0003,
          low: open * 0.9996,
        });
        j--;
      }
    }

    prevTime = time;
    newPrices.push(prices[i]);
  }

  return newPrices;
}

// Tokens that are not on MEXC — use CoinGecko OHLCV proxy instead.
const COINGECKO_SYMBOLS = new Set(["BESC", "WBESC", "MONEY"]);

export async function getLimitChartPricesFromStats(
  chainId: number,
  symbol: string,
  period: string,
  _limit = 1
): Promise<FromOldToNewArray<Bar>> {
  if (["WBTC", "WETH", "WRON", "WRON"].includes(symbol)) {
    symbol = symbol.substr(1);
  } else if (symbol === "BTC.b") {
    symbol = "BTC";
  }
  const sym = symbol.toUpperCase();
  // Stock/commodity synthetics → BESC indexer (Yahoo Finance), never MEXC
  if (STOCK_SYMBOLS.has(sym)) {
    return getStockChartPrices(symbol, period, _limit || 300);
  }
  // Route BESC-native tokens to CoinGecko, everything else to MEXC.
  if (COINGECKO_SYMBOLS.has(sym)) {
    return getChartPricesFromCoinGecko(symbol, period);
  }
  return getChartPricesFromMexc(chainId, symbol, period);
}

export async function getChartPricesFromStats(
  chainId: number,
  symbol: string,
  period: string
): Promise<FromOldToNewArray<Bar>> {
  if (["WBTC", "WETH", "WRON", "WRON"].includes(symbol)) {
    symbol = symbol.substr(1);
  } else if (symbol === "BTC.b") {
    symbol = "BTC";
  }
  const sym = symbol.toUpperCase();
  if (STOCK_SYMBOLS.has(sym)) {
    return getStockChartPrices(symbol, period);
  }
  if (COINGECKO_SYMBOLS.has(sym)) {
    return getChartPricesFromCoinGecko(symbol, period);
  }
  return getChartPricesFromMexc(chainId, symbol, period);
}


const PERIOD_TO_RESOLUTION: Record<string, string> = {
  "1m": "1",
  "5m": "5",
  "15m": "15",
  "1h": "60",
  "4h": "240",
  "1d": "D",
};

export function getChainlinkChartPricesFromGraph(tokenSymbol: string, period: string): Promise<FromOldToNewArray<Bar>> {
  const normalizedSymbol = getNormalizedTokenSymbol(tokenSymbol);
  const token = getTokenBySymbolSafe(BSC, normalizedSymbol);
  if (!token) {
    return Promise.resolve([]);
  }

  const resolution = PERIOD_TO_RESOLUTION[period] ?? "60";
  const periodSeconds = CHART_PERIODS[period] ?? 3600;
  const from = Math.floor(Date.now() / 1000) - periodSeconds * 1000;

  return fetchCandles(token.address, { resolution, from })
    .then((candles) => candles as unknown as FromOldToNewArray<Bar>)
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error(err);
      return [];
    });
}

// In-memory cache for CoinGecko responses — keyed by symbol+period, TTL 5 min.
// Prevents the chart from hammering the free-tier API limit (429s) on re-renders.
const _cgCache: Map<string, { bars: FromOldToNewArray<Bar>; ts: number }> = new Map();
const CG_CACHE_TTL = 5 * 60 * 1000;

// Fetch OHLCV for BESC-native tokens (BESC/WBESC, MONEY) via CoinGecko proxy.
// The proxy converts CoinGecko price-point stream → MEXC klines format.
export async function getChartPricesFromCoinGecko(symbol: string, period: string): Promise<FromOldToNewArray<Bar>> {
  const cacheKey = `${symbol.toUpperCase()}-${period}`;
  const hit = _cgCache.get(cacheKey);
  if (hit && Date.now() - hit.ts < CG_CACHE_TTL) return hit.bars;

  // Round timestamps to nearest 5-minute bucket so concurrent calls share the same URL.
  const BUCKET = 5 * 60 * 1000;
  const endTime = Math.floor(Date.now() / BUCKET) * BUCKET;
  const startTime = endTime - (CHART_PERIODS[period] || 3600) * 150 * 1000;
  const apiUrl = `${COINGECKO_API_URL}?symbol=${symbol.toUpperCase()}&start=${startTime}&end=${endTime}`;

  const TIMEOUT = 8000;
  const res = await new Promise<Response>(async (resolve, reject) => {
    let done = false;
    setTimeout(() => { done = true; reject(new Error(`CoinGecko timeout ${apiUrl}`)); }, TIMEOUT);
    try {
      const r = await fetch(apiUrl);
      if (!done) resolve(r);
    } catch (ex) {
      if (!done) reject(ex);
    }
  });

  if (!res.ok) throw new Error(`CoinGecko request failed ${res.status}`);
  const json = await res.json();
  if (!Array.isArray(json) || !json.length) return [];

  const prices: Price[] = [];
  for (const kline of json) {
    prices.push({
      time:  kline[0] / 1000 + timezoneOffset,
      open:  parseFloat(kline[1]),
      high:  parseFloat(kline[2]),
      low:   parseFloat(kline[3]),
      close: parseFloat(kline[4]),
    });
  }
  _cgCache.set(cacheKey, { bars: prices, ts: Date.now() });
  return prices;
}

export async function getChartPricesFromMexc(chainId, symbol, period) {
  // symbol = getNormalizedTokenSymbol(symbol);
  ///// ###### test here
  const endTime = Date.now();
  let startTime = Number(endTime) - Number(CHART_PERIODS[period]) * 150 * 1000;
  const interval = MEXC_CHART_PERIODS[period];
  const apiUrl = `${MEXC_API_URL}?end=${endTime}&interval=${interval}&start=${startTime}&symbol=${symbol}USDT`;
  const TIMEOUT = 5000;
  const res = await new Promise(async (resolve, reject) => {
    let done = false;
    setTimeout(() => {
      done = true;
      reject(new Error(`request timeout ${apiUrl}`));
    }, TIMEOUT);

    let lastEx;
    for (let i = 0; i < 3; i++) {
      if (done) return;
      try {
        const res = await fetch(apiUrl);
        resolve(res);
        return;
      } catch (ex) {
        await sleep(300);
        lastEx = ex;
      }
    }
    reject(lastEx);
  });

  const response = res as Response;
  if (!response.ok) {
    throw new Error(`mexc request failed ${response.status} ${response.statusText}`);
  }

  const json = await response.json();

  if (json === undefined || json === new Array()) {
    throw new Error(`getting data error: code=${json.code}, length=${json.s}`);
  }
  const _prices: Price[] = [];

  for (let i = 0; i < json.length; i++) {
    _prices.push({
      time: json[i][0] / 1000 + timezoneOffset,
      open: parseFloat(json[i][1]),
      high: parseFloat(json[i][2]),
      low: parseFloat(json[i][3]),
      close: parseFloat(json[i][4]),
    });
  }
  
  return _prices;
}

export function useChartPrices(chainId, symbol, isStable, period, currentAveragePrice) {
  const swrKey = !isStable && symbol ? ["getChartCandles", chainId, symbol, period] : null;
  let { data: prices, mutate: updatePrices } = useSWR(swrKey, {
    fetcher: async () => {
      try {
        const value = await getChartPricesFromStats(chainId, symbol, period);
        return value
      } catch (ex) {
        // eslint-disable-next-line no-console
        console.warn(ex);
        // eslint-disable-next-line no-console
        console.warn("Switching to graph chainlink data");
        try {
          return await getChainlinkChartPricesFromGraph(symbol, period);
        } catch (ex2) {
          // eslint-disable-next-line no-console
          console.warn("getChainlinkChartPricesFromGraph failed");
          // eslint-disable-next-line no-console
          console.warn(ex2);
          return [];
        }
      }
    },
    dedupingInterval: 60000,
    focusThrottleInterval: 60000 * 10,
  });

  const currentAveragePriceString = currentAveragePrice && currentAveragePrice.toString();
  const retPrices = useMemo(() => {
    if (isStable) {
      return getStablePriceData(period);
    }

    if (!prices) {
      return [];
    }

    let _prices = [...prices];
    if (currentAveragePriceString && prices.length) {
      _prices = appendCurrentAveragePrice(_prices, BigInt(currentAveragePriceString), period);
    }

    return fillGaps(_prices, CHART_PERIODS[period]);
  }, [prices, isStable, currentAveragePriceString, period]);

  return [retPrices, updatePrices];
}

function appendCurrentAveragePrice(prices, currentAveragePrice, period) {
  const periodSeconds = CHART_PERIODS[period];
  const currentCandleTime = Math.floor(Date.now() / 1000 / periodSeconds) * periodSeconds;
  const last = prices[prices.length - 1];
  const averagePriceValue = parseFloat(formatAmount(currentAveragePrice, USD_DECIMALS, 2));
  if (currentCandleTime === last.time) {
    last.close = averagePriceValue;
    last.high = Math.max(last.open, last.high, averagePriceValue);
    last.low = Math.min(last.open, last.low, averagePriceValue);
    return prices;
  } else {
    const newCandle = {
      time: currentCandleTime,
      open: last.close,
      close: averagePriceValue,
      high: averagePriceValue,
      low: averagePriceValue,
    };
    return [...prices, newCandle];
  }
}

export function getStablePriceData(period, countBack = 100) {
  const periodSeconds = CHART_PERIODS[period];
  const now = Math.floor(Date.now() / 1000 / periodSeconds) * periodSeconds;
  let priceData: any = [];
  for (let i = countBack; i > 0; i--) {
    priceData.push({
      time: now - i * periodSeconds,
      open: 1,
      close: 1,
      high: 1,
      low: 1,
    });
  }
  return priceData;
}
